import React, { useState, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from '@/store/languageStore';
import { useAsyncData } from '@/hooks';
import type { CrmProject, DevProject, ProjectSource, ProjectTask, GanttData } from '@/types/projects';
import { ProjectKanban } from '@/components/projects/ProjectKanban';
import { ProjectGantt } from '@/components/projects/ProjectGantt';
import { TaskModal } from '@/components/projects/TaskModal';
import { ProjectEditModal } from '@/components/projects/ProjectEditModal';
import { MilestonePanel } from '@/components/projects/MilestonePanel';
import { TimeEntryPanel } from '@/components/projects/TimeEntryPanel';
import { ArrowLeftIcon, PencilSquareIcon } from '@heroicons/react/24/outline';

type Tab = 'overview' | 'kanban' | 'gantt' | 'milestones' | 'time';

interface Props {
  source: ProjectSource;
  listRoute: string;
}

export const ProjectDetailPage: React.FC<Props> = ({ source, listRoute }) => {
  const { id } = useParams<{ id: string }>();
  const projectId = parseInt(id || '0');
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [tab, setTab] = useState<Tab>('overview');
  const [taskModal, setTaskModal] = useState<ProjectTask | null | undefined>(undefined);
  const [showEditProject, setShowEditProject] = useState(false);

  const baseUrl = source === 'crm' ? `/api/crm/projects/${projectId}` : `/api/admin/dev-projects/${projectId}`;

  const { data: project, refetch: refetchProject } = useAsyncData<CrmProject | DevProject>(
    baseUrl,
    { skip: !projectId }
  );

  const { data: tasks, refetch: refetchTasks } = useAsyncData<ProjectTask[]>(
    `${baseUrl}/tasks`,
    { initialData: [], skip: !projectId }
  );

  const { data: milestones, refetch: refetchMilestones } = useAsyncData<any[]>(
    `${baseUrl}/milestones`,
    { initialData: [], skip: !projectId }
  );

  const { data: timeEntries, refetch: refetchTime } = useAsyncData<any[]>(
    `${baseUrl}/time-entries`,
    { initialData: [], skip: !projectId }
  );

  const { data: ganttData, refetch: refetchGantt } = useAsyncData<GanttData>(
    `${baseUrl}/gantt`,
    { skip: !projectId || tab !== 'gantt' }
  );

  const refreshAll = useCallback(() => {
    refetchProject();
    refetchTasks();
    refetchMilestones();
    refetchTime();
    refetchGantt();
  }, [refetchProject, refetchTasks, refetchMilestones, refetchTime, refetchGantt]);

  if (!project) {
    return (
      <div className="p-4">
        <button onClick={() => navigate(listRoute)} className="flex items-center gap-2 text-textSecondary">
          <ArrowLeftIcon className="w-5 h-5" />
          {t('common.back')}
        </button>
        <p className="mt-4">{t('common.loading')}</p>
      </div>
    );
  }

  const crm = source === 'crm' ? (project as CrmProject) : null;
  const dev = source === 'dev' ? (project as DevProject) : null;

  const tabs: { id: Tab; label: string }[] = [
    { id: 'overview', label: t('projects.tabs.overview') },
    { id: 'kanban', label: t('projects.tabs.kanban') },
    { id: 'gantt', label: t('projects.tabs.gantt') },
    { id: 'milestones', label: t('projects.tabs.milestones') },
    { id: 'time', label: t('projects.tabs.time') },
  ];

  return (
    <div className="p-4 bg-bgContainer h-full overflow-auto">
      <button onClick={() => navigate(listRoute)} className="flex items-center gap-2 mb-4 text-textSecondary hover:text-textDefault">
        <ArrowLeftIcon className="w-5 h-5" />
        {t('common.back')}
      </button>

      <div className="mb-6">
        <h1 className="text-2xl font-bold text-textDefault">{project.name}</h1>
        <p className="text-textSecondary mt-1">
          {crm?.customer_name}
          {dev?.release_target && ` · ${dev.release_target}`}
          {' · '}
          <span className="capitalize">{t(`projects.status.${project.status}`)}</span>
        </p>
        {project.description && (
          <p className="text-sm text-textSecondary mt-2">{project.description}</p>
        )}
      </div>

      <div className="flex gap-1 mb-6 border-b border-border overflow-x-auto">
        {tabs.map(({ id, label }) => (
          <button
            key={id}
            type="button"
            onClick={() => setTab(id)}
            className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px whitespace-nowrap ${
              tab === id ? 'border-primary text-primary' : 'border-transparent text-textSecondary hover:text-textDefault'
            }`}
          >
            {label}
          </button>
        ))}
      </div>

      {tab === 'overview' && (
        <div className="grid md:grid-cols-2 gap-6">
          <div className="space-y-3 text-sm">
            <div className="flex items-center justify-between gap-3">
              <h3 className="font-semibold">{t('projects.tabs.overview')}</h3>
              <button
                type="button"
                onClick={() => setShowEditProject(true)}
                className="flex items-center gap-1.5 px-3 py-1.5 text-sm bg-primary text-white rounded-lg hover:opacity-90 shrink-0"
              >
                <PencilSquareIcon className="w-4 h-4" />
                {t('projects.editProject')}
              </button>
            </div>
            {crm && (
              <>
                <Row label={t('projects.budget')} value={crm.budget != null ? `${crm.budget.toLocaleString('sv-SE')} kr` : '—'} />
                <Row label={t('projects.billingType')} value={t(`projects.billingTypes.${crm.billing_type || 'fixed'}`)} />
                {crm.billing_type === 'hourly' && (
                  <Row
                    label={t('projects.hourlyRate')}
                    value={crm.hourly_rate != null ? `${crm.hourly_rate.toLocaleString('sv-SE')} kr/h` : '—'}
                  />
                )}
                <Row label={t('projects.actualCost')} value={crm.actual_cost != null ? `${crm.actual_cost.toLocaleString('sv-SE')} kr` : '—'} />
                <Row label={t('projects.projectType')} value={t(`projects.types.${crm.project_type}`)} />
                <Row label={t('projects.manager')} value={crm.project_manager_name || '—'} />
                <Row
                  label={t('projects.primaryContact')}
                  value={
                    crm.primary_contact_name
                      ? [crm.primary_contact_name, crm.primary_contact_email, crm.primary_contact_phone]
                          .filter(Boolean)
                          .join(' · ')
                      : '—'
                  }
                />
              </>
            )}
            {dev && (
              <>
                <Row label={t('projects.releaseTarget')} value={dev.release_target || '—'} />
                <Row label={t('projects.owner')} value={dev.owner_name || '—'} />
                <Row label={t('projects.priority')} value={t(`projects.priorityLevel.${dev.priority}`)} />
              </>
            )}
            <Row label={t('projects.plannedStart')} value={project.planned_start?.slice(0, 10) || '—'} />
            <Row label={t('projects.plannedEnd')} value={project.planned_end?.slice(0, 10) || '—'} />
            <Row label={t('projects.openTasks')} value={String((tasks || []).filter((t) => !['done', 'cancelled'].includes(t.status)).length)} />
          </div>
          <MilestonePanel
            source={source}
            projectId={projectId}
            milestones={milestones || []}
            onRefresh={refreshAll}
          />
        </div>
      )}

      {tab === 'kanban' && (
        <ProjectKanban
          source={source}
          projectId={projectId}
          tasks={tasks || []}
          onRefresh={refetchTasks}
          onEditTask={(task) => setTaskModal(task ?? null)}
        />
      )}

      {tab === 'gantt' && ganttData && (
        <ProjectGantt
          data={ganttData}
          onTaskClick={(task) => setTaskModal(task)}
        />
      )}

      {tab === 'milestones' && (
        <MilestonePanel
          source={source}
          projectId={projectId}
          milestones={milestones || []}
          onRefresh={refreshAll}
        />
      )}

      {tab === 'time' && (
        <TimeEntryPanel
          source={source}
          projectId={projectId}
          entries={timeEntries || []}
          tasks={tasks || []}
          onRefresh={refreshAll}
          showBudget={source === 'crm'}
          budget={crm?.budget}
          actualCost={crm?.actual_cost}
        />
      )}

      {taskModal !== undefined && (
        <TaskModal
          source={source}
          projectId={projectId}
          customerId={crm?.customer_id}
          task={taskModal}
          milestones={milestones || []}
          allTasks={tasks || []}
          onClose={() => setTaskModal(undefined)}
          onSaved={refreshAll}
        />
      )}

      {showEditProject && (
        <ProjectEditModal
          source={source}
          project={project}
          onClose={() => setShowEditProject(false)}
          onSaved={refetchProject}
        />
      )}
    </div>
  );
};

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex justify-between py-1 border-b border-border/50">
      <span className="text-textSecondary">{label}</span>
      <span className="text-textDefault">{value}</span>
    </div>
  );
}
